import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Chip } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { CopyInput, EntityCard, Section } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { CardRepository } from '~/repositories/CardRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';
import { formatDateTime } from '~/util';

import { CardProps } from './Card';

interface Params {
  id: string;
}

const cardQueryKey = (id: string) => ['card', id] as const;

const StatusChip = styled(Chip)<{ $status: string }>`
  text-transform: capitalize;
`;

const TypeChip = styled(Chip)`
  text-transform: capitalize;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  align-items: start;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};

  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.foreground};
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const formatStatus = (status: string): string => {
  if (!status) return '-';
  return status.toLowerCase().replace(/_/g, ' ');
};

const formatType = (type: string): string => {
  if (!type) return '-';
  return type.toLowerCase().replace(/_/g, ' ');
};

export const PageCardInformation: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { card: outletCard } = useOutletContext<CardProps>();
  const [cardProgram, setCardProgram] = useState<CardProgram | null>(null);

  const { data: card, error: cardError } = useQuery({
    queryKey: cardQueryKey(id!),
    queryFn: () => CardRepository.get(id!),
    enabled: !!id && !outletCard,
    initialData: outletCard,
  });

  // Fetch card program details
  useEffect(() => {
    const fetchCardProgram = async () => {
      if (!card?.cardProgramId) return;
      try {
        const response = await CardProgramRepository.listCardPrograms();
        const program = response.cardPrograms?.find((p) => p.id === card.cardProgramId);
        if (program) {
          setCardProgram(program);
        }
      } catch (err) {
        console.error('Error fetching card program:', err);
      }
    };
    fetchCardProgram();
  }, [card?.cardProgramId]);

  useEffect(() => {
    if (cardError) {
      navigate(ROUTE.CARDS);
      addDangerNotification({
        content: (cardError as any).message || 'Failed to load card',
      });
    }
  }, [cardError, navigate, addDangerNotification]);

  if (!card) {
    return null;
  }

  return (
    <Inner p={0}>
      <Section headline="Information">
        <Inner px="var(--page-margin)" py={16}>
          <FieldsContainer>
            {card.authorizedUserEntityId && (
              <FieldRow>
                <FieldLabel>Authorized User</FieldLabel>
                <FieldValue>
                  <EntityCard entityId={card.authorizedUserEntityId} />
                </FieldValue>
              </FieldRow>
            )}

            <FieldRow>
              <FieldLabel>Card Program</FieldLabel>
              <FieldValue>
                <CopyInput value={cardProgram?.description || card.cardProgramId || '-'} />
              </FieldValue>
            </FieldRow>

            {card.cardAccountId && (
              <FieldRow>
                <FieldLabel>Card Account ID</FieldLabel>
                <FieldValue>
                  <CopyInput value={card.cardAccountId} isMonospaced />
                </FieldValue>
              </FieldRow>
            )}

            <FieldRow>
              <FieldLabel>BIN</FieldLabel>
              <FieldValue>
                <CopyInput value={card.bin || '-'} isMonospaced />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Last Four Digits</FieldLabel>
              <FieldValue>
                <CopyInput value={card.lastFourDigits || '-'} isMonospaced />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Expires</FieldLabel>
              <FieldValue>
                <CopyInput value={card.expiresAt || '-'} />
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Status</FieldLabel>
              <FieldValue>
                <StatusChip $status={card.status || ''}>{formatStatus(card.status || '')}</StatusChip>
              </FieldValue>
            </FieldRow>

            <FieldRow>
              <FieldLabel>Type</FieldLabel>
              <FieldValue>
                <TypeChip>{formatType(card.type || '')}</TypeChip>
              </FieldValue>
            </FieldRow>

            {card.createdAt && (
              <FieldRow>
                <FieldLabel>Created At</FieldLabel>
                <FieldValue>
                  <CopyInput value={formatDateTime(card.createdAt, { timeZoneFormat: 'utc' })} />
                </FieldValue>
              </FieldRow>
            )}
          </FieldsContainer>
        </Inner>
      </Section>
    </Inner>
  );
};
